import { Button, Flex, Pagination, Space, Spin, Table, Typography, type TableProps } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { EmailMessageService } from '@services/EmailMessageService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { EmailMessage } from '../../../types'

export const EmailMessagesDashboardPage = () => {
    const confirm = useConfirmation()
    const { showNotification } = useNotifications()

    const [emails, setEmails] = useState<EmailMessage[]>()
    const [totalCount, setTotalCount] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [offset, setOffset] = useState(0)
    const [showTestEmailButton, setShowTestEmailButton] = useState<boolean>(false)

    const limit = 50 // Set a constant limit per page

    useEffect(() => {
        if (import.meta.env.VITE_NODE_ENV === 'development') {
            setShowTestEmailButton(true)
        }
    }, [])

    const fetch = async ({ searchOffset }: { searchOffset: number }) => {
        try {
            setIsLoading(true)
            const results = await EmailMessageService.list({
                offset: searchOffset,
                limit,
                sortBy: 'createdAt',
                sortOrder: 'desc'
            })

            setEmails(results.emails)
            setTotalCount(results.totalCount)
        } catch (e) {
            console.error('Error retrieving emails', e)
        } finally {
            setIsLoading(false)
        }
        setIsLoading(false)
    }

    useEffect(() => {
        fetch({ searchOffset: offset })
    }, [])

    const checkEmailQueue = async () => {
        if (
            await confirm({
                title: 'Check Email Queue',
                description: 'A cron job already performs this check. Confirming will attempt to send the next 100'
            })
        ) {
            await EmailMessageService.checkEmailQueue()

            showNotification({
                title: 'Sent'
            })

            fetch({ searchOffset: offset })
        }
    }

    const testEmailQueue = async () => {
        if (
            await confirm({
                title: 'Add emails to test',
                description: 'This will send only to Elton'
            })
        ) {
            await EmailMessageService.testQueue()

            showNotification({
                title: 'Test emails queued'
            })

            fetch({ searchOffset: offset })
        }
    }

    const sendEmail = async (id: string) => {
        if (
            await confirm({
                title: 'Send email?'
            })
        ) {
            await EmailMessageService.send(id)

            showNotification({
                title: 'Sent'
            })

            fetch({ searchOffset: offset })
        }
    }

    const columns: TableProps<EmailMessage>['columns'] = [
        {
            title: 'To',
            dataIndex: 'toAddress',
            key: 'toAddress'
        },
        {
            title: 'Subject',
            dataIndex: 'subject',
            key: 'subject'
        },
        {
            title: 'Queued At',
            key: 'createdAt',
            render: ({ createdAt }) => <>{dayjs(createdAt).format('MMMM D, YYYY h:mma')}</>,
            sorter: true
        },
        {
            title: 'Attempts',
            dataIndex: 'attempts',
            key: 'attempts',
            sorter: true
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="link"
                        onClick={async () => {
                            await sendEmail(record.id)

                            fetch({ searchOffset: offset })
                        }}
                    >
                        Send
                    </Button>
                </Space>
            )
        }
    ]

    if (!emails) return <Spin />

    return (
        <>
            <Typography.Title>Email Queue</Typography.Title>
            <Flex justify="space-between">
                <Button type="primary" onClick={checkEmailQueue}>
                    Manually send next 100
                </Button>
                {showTestEmailButton && (
                    <Button type="primary" onClick={testEmailQueue}>
                        Add Test Emails
                    </Button>
                )}
            </Flex>
            <Flex gap="middle" vertical>
                <p>All emails listed have not yet been sent</p>

                {isLoading && <Spin />}
                {!isLoading && <Table columns={columns} dataSource={emails} rowKey="userId" pagination={false} />}

                <Pagination
                    current={Math.floor(offset / limit) + 1}
                    onChange={async page => {
                        const newOffset = (page - 1) * limit
                        setOffset(newOffset)
                        await fetch({
                            searchOffset: newOffset
                        })
                    }}
                    total={totalCount}
                    pageSize={limit}
                />
                <Flex justify="space-between" align="center">
                    <Typography.Text style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        Total: {totalCount}
                    </Typography.Text>
                </Flex>
            </Flex>
        </>
    )
}
